import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState, type FormEvent, type ReactNode } from 'react';
import { track } from '../lib/amplitude';
import { errorMessage } from '../lib/api';
import { shortDate } from '../lib/format';
import type { Comp, CompBody } from '../queries/admin';
import { Badge, Banner, Button, Input, cx } from '../components/ui';

/**
 * Handing out the paid plan, and taking it back. One card, used for a person
 * and for a team, because the decision is the same shape either way: give it,
 * say why, optionally say until when, and for a team how many seats.
 *
 * The grant is not a Stripe subscription and does not pretend to be one. It
 * lives in its own columns on the API side, so nothing Stripe later says
 * about the account can undo it, and the panel always shows both: what Stripe
 * thinks, and what we decided.
 */

/** `<input type="date">` speaks YYYY-MM-DD; the API wants an instant. */
function isoFromDateInput(value: string): string | null {
  if (!value) return null;
  // The end of that day, locally: "until the 31st" should include the 31st.
  const date = new Date(`${value}T23:59:59`);
  return Number.isNaN(date.getTime()) ? null : date.toISOString();
}

function dateInputFromIso(value: string | null): string {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  const local = new Date(date.getTime() - date.getTimezoneOffset() * 60_000);
  return local.toISOString().slice(0, 10);
}

/** Tomorrow: the earliest expiry that is not already in the past. */
function tomorrow(): string {
  return dateInputFromIso(new Date(Date.now() + 86_400_000).toISOString());
}

/** The one-word state of a grant, for a table cell or beside a heading. */
export function CompBadge({ comp }: { comp: Comp | null }) {
  if (!comp) return null;
  return <Badge tone={comp.active ? 'go' : 'neutral'}>{comp.active ? 'Comped' : 'Comp lapsed'}</Badge>;
}

/** Who, when, until when, how many seats, and why: one line, in that order. */
function CompSummary({ comp }: { comp: Comp }) {
  const parts = [
    comp.by,
    shortDate(comp.at),
    comp.until ? `${comp.active ? 'until' : 'ended'} ${shortDate(comp.until)}` : 'no expiry',
    comp.seats !== null ? `${comp.seats} ${comp.seats === 1 ? 'seat' : 'seats'}` : null,
  ].filter(Boolean);
  return (
    <p className="text-caption text-chalk-faint">
      {parts.join(' · ')}
      {comp.reason ? <span className="text-chalk-dim"> · {comp.reason}</span> : null}
    </p>
  );
}

export function CompCard({
  title,
  blurb,
  state,
  target,
  comp,
  grant,
  revoke,
  invalidates,
  seats: seatsField,
}: {
  title: string;
  /** What comping this thing actually does, in one sentence. */
  blurb: ReactNode;
  /** Where they stand right now, drawn by the caller: it differs per page. */
  state: ReactNode;
  /** What is being comped, for analytics. The card is the same either way. */
  target: 'user' | 'team';
  comp: Comp | null;
  grant: (body: CompBody) => Promise<unknown>;
  revoke: () => Promise<unknown>;
  /** Query keys to refetch once the grant changes. */
  invalidates: ReadonlyArray<readonly unknown[]>;
  /**
   * Teams only. A person is one seat, so their card has no such field; a
   * team's says how many seats the grant covers, on top of any it buys.
   */
  seats?: { hint: ReactNode };
}) {
  const queryClient = useQueryClient();
  const [until, setUntil] = useState(() => dateInputFromIso(comp?.until ?? null));
  const [reason, setReason] = useState(comp?.reason ?? '');
  const [seats, setSeats] = useState(comp?.seats != null ? String(comp.seats) : '');
  const [confirming, setConfirming] = useState(false);

  const refresh = () =>
    Promise.all(invalidates.map((queryKey) => queryClient.invalidateQueries({ queryKey: [...queryKey] })));

  const give = useMutation({
    mutationFn: () =>
      grant({
        until: isoFromDateInput(until),
        reason: reason.trim() || null,
        ...(seatsField ? { seats: seats.trim() === '' ? null : Number(seats) } : {}),
      }),
    onSuccess: () => {
      track('Comp Granted', { target });
      return refresh();
    },
  });
  const take = useMutation({
    mutationFn: revoke,
    onSuccess: () => {
      track('Comp Revoked', { target });
      setConfirming(false);
      setUntil('');
      setReason('');
      setSeats('');
      return refresh();
    },
  });

  const live = comp?.active ?? false;
  const submit = (event: FormEvent) => {
    event.preventDefault();
    give.mutate();
  };

  return (
    <form onSubmit={submit} className={cx('card overflow-hidden', live && 'border-go/30')}>
      <div className="px-4 pt-4">
        <div className="flex flex-wrap items-start justify-between gap-2">
          <div className="min-w-0">
            <h2 className="text-h4 text-chalk">{title}</h2>
            <p className="mt-1 text-body text-chalk-dim">{state}</p>
          </div>
          <CompBadge comp={comp} />
        </div>
        {comp ? (
          <div className="mt-2">
            <CompSummary comp={comp} />
          </div>
        ) : null}
      </div>

      <div className="mt-4 border-t border-hairline px-4 py-4">
        {/* Every field the same height: no per-field hint, one hint line under
            the row, so the inputs and the labels above them actually line up. */}
        <div
          className={cx(
            'grid grid-cols-1 gap-3 sm:items-end',
            seatsField ? 'sm:grid-cols-[7rem_11rem_1fr]' : 'sm:grid-cols-[11rem_1fr]',
          )}
        >
          {seatsField ? (
            <Input
              label="Seats"
              type="number"
              min={1}
              max={1000}
              step={1}
              inputMode="numeric"
              placeholder="none"
              value={seats}
              onChange={(event) => setSeats(event.target.value)}
            />
          ) : null}
          <Input label="Expires" type="date" min={tomorrow()} value={until} onChange={(event) => setUntil(event.target.value)} />
          <Input
            label="Reason"
            placeholder="Friends and family, support apology, agreed by email…"
            maxLength={200}
            value={reason}
            onChange={(event) => setReason(event.target.value)}
          />
        </div>
        <p className="mt-2 text-caption text-chalk-faint">
          {seatsField ? <>{seatsField.hint} Blank expiry never lapses.</> : <>A blank expiry never lapses.</>}
        </p>
        <p className="mt-1 text-caption text-chalk-faint">{blurb}</p>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-2 border-t border-hairline bg-slate-900/40 px-4 py-3">
        <div className="flex flex-wrap items-center gap-2">
          {comp ? (
            confirming ? (
              <>
                <Button size="sm" variant="danger" busy={take.isPending} onClick={() => take.mutate()}>
                  Yes, take it back
                </Button>
                <Button size="sm" variant="ghost" onClick={() => setConfirming(false)} disabled={take.isPending}>
                  Keep it
                </Button>
              </>
            ) : (
              <Button size="sm" variant="ghost" onClick={() => setConfirming(true)}>
                {live ? 'Revoke' : 'Clear this record'}
              </Button>
            )
          ) : null}
        </div>
        <Button type="submit" variant={live ? 'secondary' : 'primary'} busy={give.isPending}>
          {live ? 'Update grant' : 'Grant premium'}
        </Button>
      </div>

      {give.isError || take.isError ? (
        <div className="px-4 pb-4">
          <Banner tone="danger">{errorMessage(give.error ?? take.error)}</Banner>
        </div>
      ) : null}
    </form>
  );
}
